import { useEffect, useState, useCallback, useMemo } from "react";
import { t } from "i18next";
import {
  SearchOptions,
  SearchQuery,
  searchWithFilters,
  toSearchOrder,
} from "../../services/api/search";
import { api } from "../../services/api/api";
import { showApiErrorToast } from "../../services/api/error";
import { Pagination } from "../../components/pagination/Pagination";
import { GenericListLayout } from "../../components/generic-list-view/GenericListLayout";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
import ExportButton from "../../components/export-button/ExportButton";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import ReplenishmentsTable from "./sections/ReplenishmentsTable";
import ReplenishmentFilterForm, { getQueriesFromFilter } from "./filter/ReplenishmentFilterForm";
import ReplenishmentForm from "./ReplenishmentForm";
import { useReplenishmentsFilters } from "../../hooks/filters/useReplenishmentsFilters";
import { replenishmentExportDescriptor } from "../../services/export/data/replenishmentExport";
import toast from "react-hot-toast";
import "./Replenishments.scss";
import { useTablePageSize } from "../../hooks/useTablePageSize";

/**
 * Composant principal pour la gestion des demandes de pièces
 * Gère l'affichage, le filtrage, la pagination et l'export des demandes
 */
export function Replenishments() {
  const [replenishmentsPageState, setReplenishmentsPageState] = useState<EasyTech.Replenishment[]>([]);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [pageState, setPageState] = useState<number>(0);
  const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "created", orderDesc: true });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newReplenishment, setNewReplenishment] = useState<Partial<EasyTech.Replenishment>>({
    parts: [],
    priority: "NORMAL"
  });

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({ head, orderDesc });
  };

  /**
   * Créer une nouvelle demande de pièces
   */
  const handleCreateReplenishment = async (data: any): Promise<void> => {
    try {
      if (!data.technician || !data.parts || data.parts.length === 0) {
        toast.error(t("replenishment:errors.required"));
        return Promise.reject();
      }

      const payload = {
        technician: {
          id: data.technician.id
        },
        parts: data.parts.map((p: EasyTech.ReplenishmentPart) => ({
          part: {
            id: p.part.id
          },
          quantity: p.quantity
        })),
        priority: data.priority || "NORMAL",
        comments: data.comments || null,
        ...(data.technician.van && { van: { id: data.technician.van.id } })
      };

      await api.post("users/replenishments", payload);
      toast.success(t("replenishment:messages.created"));
      setNewReplenishment({ parts: [], priority: "NORMAL" });

      // Reload data
      await loadReplenishmentsWithFilters();
    } catch (error) {
      showApiErrorToast(error);
      throw error;
    }
  };

  // Fonction pour réinitialiser la page
  const resetPage = useCallback(() => {
    setPageState(0);
  }, []);

  // Utilisation du hook de filtres
  const {
    filters,
    handleFilterSubmit,
    clearFilter: handleFilterClear,
    clearAllFilters,
    displayFilters
  } = useReplenishmentsFilters(resetPage);

  /**
   * Charge les demandes filtrées depuis l'API
   */
  /** Le tri affiche. Une seule definition, pour que l'export ne puisse pas diverger de la table. */
  const searchOrder = useMemo(
    () => toSearchOrder(orderState.head, orderState.orderDesc),
    [orderState]
  );

  const loadReplenishmentsWithFilters = useCallback(async () => {
    if (!filters) return;
    setIsLoading(true);
    try {
      // Construction des critères de recherche en fonction des filtres actifs
      const queries: SearchQuery[] = getQueriesFromFilter(filters);

      // Configuration des options de recherche (pagination, tri, filtres)
      const searchOptions: SearchOptions = {
        paginate: {
          pageSize,
          pageNumber: pageState,
        },
        order: searchOrder,
        queries,
      };

      try {
        // Appel à l'API de recherche
        const response = await searchWithFilters<EasyTech.Replenishment>(
          "replenishments",
          searchOptions
        );

        // Traitement des résultats
        if (response && response.data && Array.isArray(response.data)) {
          setReplenishmentsPageState(response.data);
          setPageNumberState(response.totalPages);
        } else {
          // Gestion du cas où la réponse est invalide
          setReplenishmentsPageState([]);
          setPageNumberState(1);
        }
      } catch (searchError) {
        // Gestion des erreurs d'API
        setReplenishmentsPageState([]);
        setPageNumberState(1);
      }
    } catch (error) {
      // Gestion des erreurs générales
      setReplenishmentsPageState([]);
      setPageNumberState(1);
    } finally {
      setIsLoading(false);
    }
  }, [searchOrder, filters, pageState, pageSize]);

  /**
   * Charge initialement toutes les demandes depuis l'API
   */
  useEffect(() => {
    loadReplenishmentsWithFilters();
  }, [loadReplenishmentsWithFilters]);

  const headerActions = (
    <ExportButton<EasyTech.Replenishment>
      filters={filters}
      entityName="replenishments"
      fileName="replenishments.xlsx"
      descriptor={replenishmentExportDescriptor}
      order={searchOrder}
      getQueryFromFilter={getQueriesFromFilter}
    />
  );

  const createDrawerComponent = (
    <EditCreateDrawer<EasyTech.Replenishment>
      data={newReplenishment as EasyTech.Replenishment}
      entityName={t("replenishment:title")}
      FormComponent={ReplenishmentForm}
      onSubmit={handleCreateReplenishment}
      isNotEditable={true}
      isNotCreatable={false}
    />
  );

  const filterDrawerComponent = (
    <FilterDrawer<Partial<EasyTech.Replenishment>>
      FormComponent={ReplenishmentFilterForm}
      onSubmit={(data) => {
        // TODO: Refacto - Cette logique particulière de validation de date de RangeDatePicker a été ajoutée ici pour pallier le fait que le form est soumis depuis le bouton du Parent (FilterDrawer) sans déclencher la validation du formulaire de manière native.
        if (data.startDate && !data.endDate) return Promise.reject();
        if (data.endDate && !data.startDate) return Promise.reject();
        return handleFilterSubmit(data);
      }}
      entityName="Replenishments"
      data={filters}
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={displayFilters}
      onClearFilter={(field, newValue) => {
        // Quand on supprime le badge dateRange, on supprime startDate et endDate
        if (field === "dateRange") {
          handleFilterClear("startDate", null);
          handleFilterClear("endDate", null);
          return;
        }
        handleFilterClear(field, newValue);
      }}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
      entity="replenishment"
    />
  );

  const mainContent = replenishmentsPageState && replenishmentsPageState.length > 0 ? (
    <ReplenishmentsTable
      replenishments={replenishmentsPageState}
      onHeadClick={onOrderChange}
      selectedHead={orderState.head}
      orderDesc={orderState.orderDesc}
    />
  ) : (
    <div className="text-center py-8 bg-blue-gray-50 rounded-lg">
      <p className="text-lg text-blue-gray-700">
        {t("replenishment:messages.noData")}
      </p>
      <p className="text-sm text-blue-gray-500 mt-2">
        {filters && Object.keys(filters).length > 0
          ? t("replenishment:messages.noData.filters")
          : t("replenishment:messages.noData.add")}
      </p>
    </div>
  );

  const paginationComponent = replenishmentsPageState && replenishmentsPageState.length > 0 ? (
    <Pagination
      actualPage={pageState}
      pageNumber={pageNumberState}
      changePage={setPageState}
    />
  ) : null;

  return (
    <GenericListLayout
      entityName={t("replenishment:title")}
      path="/replenishments"
      isLoading={isLoading}
      headerActions={headerActions}
      filterDrawer={filterDrawerComponent}
      filterBadges={filterBadgesComponent}
      createDrawer={createDrawerComponent}
      content={mainContent}
      pagination={paginationComponent}
      className="replenishments"
      showBreadcrumb={true}
    />
  );
}
